import { Col, List, Row, Skeleton, Typography } from 'antd';
import { BlockLoader } from 'components/atoms/BlockLoader';
import NoResults from 'components/atoms/noResults/noResults';
import { AddressCard } from 'components/molecules/AddressCard';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll';
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGetAddressesQuery } from 'redux/services/chuckieSue/addressesApi';
import { incrementOffset } from 'redux/services/chuckieSue/addressesParams';
import { ReduxState } from 'redux/store';

/**
 * Loading skeleton for address cards while fetching more data
 */
const LoadingMoreSkeleton = (): JSX.Element => (
  <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Col key={i} span={24 / 5}>
        <Skeleton.Button active block style={{ height: 200, borderRadius: 8 }} />
      </Col>
    ))}
  </Row>
);

/**
 * Sentinel element that triggers loading more when it comes into view
 */
const ScrollSentinel = ({
  sentinelRef,
  isLoading,
  hasMore
}: {
  sentinelRef: React.RefObject<HTMLDivElement>;
  isLoading: boolean;
  hasMore: boolean;
}): JSX.Element => (
  <div
    ref={sentinelRef}
    style={{
      height: 20,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: 16,
      marginBottom: 32
    }}
  >
    {isLoading && hasMore && (
      <Typography.Text type="secondary">Loading more addresses...</Typography.Text>
    )}
    {!hasMore && (
      <Typography.Text type="secondary" style={{ fontStyle: 'italic' }}>
        All addresses loaded
      </Typography.Text>
    )}
  </div>
);

export const AddressList = (): JSX.Element => {
  const { company } = useSelector((state: ReduxState) => state.app);
  const dispatch = useDispatch();
  const { chuckieSueAddressesParams } = useSelector((state: ReduxState) => state);
  const prevOffsetRef = useRef(chuckieSueAddressesParams.offset);

  const { data, isLoading, isFetching, isError, refetch } = useGetAddressesQuery(
    {
      bussinessId: company as string,
      params: { ...chuckieSueAddressesParams, orderByField: 'code' }
    },
    { skip: !company }
  );

  const hasMore = Boolean(data && data.totalCount > data.data.length);
  const isLoadingMore = isFetching && chuckieSueAddressesParams.offset > 0;

  // Infinite scroll hook with Intersection Observer
  const { sentinelRef, scrollToTop } = useInfiniteScroll({
    hasMore,
    isLoading: isFetching,
    onLoadMore: () => dispatch(incrementOffset()),
    threshold: 300 // Start loading 300px before reaching bottom
  });

  // Scroll to top when filters change (offset resets to 0)
  useEffect(() => {
    if (prevOffsetRef.current !== 0 && chuckieSueAddressesParams.offset === 0) {
      scrollToTop();
    }
    prevOffsetRef.current = chuckieSueAddressesParams.offset;
  }, [chuckieSueAddressesParams.offset, scrollToTop]);

  /* ******************** Render States ******************** */

  // No company selected
  if (company === null) {
    return (
      <Row justify="center" align="middle" style={{ marginTop: 100 }}>
        <Col>
          <Typography.Title level={2} style={{ fontWeight: 300, color: 'rgba(0,0,0,.8)' }}>
            Please Select A Customer...
          </Typography.Title>
        </Col>
      </Row>
    );
  }

  // Error state
  if (isError) {
    return (
      <Row justify="center" align="middle" style={{ marginTop: 100 }}>
        <Col style={{ textAlign: 'center' }}>
          <Typography.Title level={3} style={{ fontWeight: 300, color: 'rgba(0,0,0,.8)' }}>
            Error loading addresses
          </Typography.Title>
          <Typography.Link onClick={() => refetch()}>Try again</Typography.Link>
        </Col>
      </Row>
    );
  }

  // No results after search
  if (!data?.data.length && !isFetching) {
    return <NoResults errorMessage="No Results Found" />;
  }

  // Initial loading state (offset 0)
  if (isLoading && chuckieSueAddressesParams.offset === 0) {
    return <BlockLoader direction="loader loader--slideUp" />;
  }

  /* ******************** Main Render ******************** */
  return (
    <div>
      {/* Results count */}
      {data && (
        <Row style={{ marginBottom: 16 }}>
          <Typography.Text type="secondary">
            Showing {data.data.length} of {data.totalCount} addresses
          </Typography.Text>
        </Row>
      )}

      {/* Address grid */}
      <List
        grid={{ gutter: 16, column: 5 }}
        dataSource={data?.data}
        renderItem={(item): JSX.Element => <AddressCard address={item} />}
      />

      {/* Loading more skeleton */}
      {isLoadingMore && <LoadingMoreSkeleton />}

      {/* Sentinel element for infinite scroll trigger */}
      <ScrollSentinel sentinelRef={sentinelRef} isLoading={isFetching} hasMore={hasMore} />
    </div>
  );
};
